import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { getClaimKey } from '../../../../../functions/get-claim-key';
import { AutoDetail } from '../../../../../models/api/view/claim/details/auto/auto-detail';
import { AutoIBC } from '../../../../../models/api/view/claim/details/auto/auto-ibc';
import { AutoPropertyDamage } from '../../../../../models/api/view/claim/details/auto/auto-property-damage';
import { AutoRentalInfo } from '../../../../../models/api/view/claim/details/auto/auto-rental-info';
import { ClaimRow } from '../../../../../models/api/view/claim-view/claim-row';
import { BaseComponent } from '../../../../base/base.component';
import { PageHeaderComponent } from '../../../../core/labels/page-header.component';
import { TabPanelComponent } from '../../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../../core/panels/tab-panel/tab.component';
import { CasualtyComponent } from '../casualty/casualty.component';
import { IbcAuComponent } from './ibc-au.component';
import { InsuredVehicleAuComponent } from './insured-vehicle-au.component';
import { LossAuComponent } from './loss-au.component';
import { PropertyAuComponent } from './property-au.component';
import { RentalInfoAuComponent } from './rental-info-au.component';
import { ThirdPartyVehicleAuComponent } from './third-party-vehicle-au.component';

/** Canadian provinces for IBC tab visibility */
const CANADIAN_PROVINCES = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
];

@Component({
  selector: 'app-auto',
  imports: [
    CommonModule,
    PageHeaderComponent,
    TabPanelComponent,
    TabComponent,
    CasualtyComponent,
    LossAuComponent,
    InsuredVehicleAuComponent,
    PropertyAuComponent,
    ThirdPartyVehicleAuComponent,
    RentalInfoAuComponent,
    IbcAuComponent,
  ],
  template: `
    <app-page-header label="Auto liability" />
    <app-tab-panel>
      <app-tab label="Event">
        <ng-template #content><app-casualty /> </ng-template>
      </app-tab>
      @if (detailsAuth) {
        <app-tab label="Loss">
          <ng-template #content>
            <app-loss-au [autoDetail]="autoDetail" />
          </ng-template>
        </app-tab>
        <app-tab label="Vehicle">
          <ng-template #content>
            <div class="tw-pt-4"></div>
            <app-page-header label="Insured vehicle" headerSize="h3" />
            <app-insured-vehicle-au [autoDetail]="autoDetail" />
            <div class="tw-pt-8"></div>
            <app-page-header label="3rd party vehicle" headerSize="h3" />
            <app-third-party-vehicle-au [autoDetail]="autoDetail" />
          </ng-template>
        </app-tab>
        <app-tab label="Property">
          <ng-template #content>
            <app-property-au [propertyDamage]="propertyDamage" />
          </ng-template>
        </app-tab>
        <app-tab label="Rental">
          <ng-template #content>
            <app-rental-info-au [rentalInfo]="rentalInfo" />
          </ng-template>
        </app-tab>
      }
      @if (showIbcTab && detailsAuth) {
        <app-tab label="IBC">
          <ng-template #content>
            <app-ibc-au [ibcData]="ibcData" />
          </ng-template>
        </app-tab>
      }
    </app-tab-panel>
  `,
  styles: ``,
})
export class AutoComponent extends BaseComponent implements OnInit {
  claimRow: ClaimRow;

  // Security
  detailsAuth = false;

  // IBC tab visibility - only for Canadian provinces
  showIbcTab = false;

  // Data from API
  autoDetail: AutoDetail;
  propertyDamage: AutoPropertyDamage;
  rentalInfo: AutoRentalInfo;
  ibcData: AutoIBC;

  async ngOnInit(): Promise<void> {
    // Security check
    this.detailsAuth = this.session.authorize('Details');

    this.claimRow = this.view.currentClaim();
    const claimKey = getClaimKey();

    // Check if IBC tab should be visible (Canadian province of loss)
    this.showIbcTab = this.isCanadianProvince(this.claimRow?.stateOfLoss);

    this.view.hideLoading();

    // Fetch all data in parallel
    const promises: Promise<any>[] = [
      this.api.auto.getDetails(claimKey),
      this.api.auto.getPropertyDamage(claimKey),
      this.api.auto.getRentalInfo(claimKey),
    ];

    // Only fetch IBC data if tab is visible
    if (this.showIbcTab) {
      promises.push(this.api.auto.getIBC(claimKey));
    }

    const results = await Promise.all(promises);

    this.autoDetail = results[0];
    this.propertyDamage = results[1];
    this.rentalInfo = results[2];

    if (this.showIbcTab) {
      this.ibcData = results[3];
    }
  }

  /**
   * Check if the state/province is a Canadian province
   */
  private isCanadianProvince(stateOfLoss: string): boolean {
    if (!stateOfLoss) return false;
    return CANADIAN_PROVINCES.includes(stateOfLoss.toUpperCase());
  }
}
